import React, { useState } from "react";
import { useMyContext } from "../../context/DataContext";
import Pdf from "../Pdffile/pdf";
import { formatMessageText } from "../../utils/FormattedText";
import { useAuth0 } from "@auth0/auth0-react";
import { getDocumentsMetadata } from "../../services/AxiosServices";

interface Message {
    user_id?: string;
    isDownloadable?: boolean;
    fileName?: string;
    text?: string;
    file?: File | null;
    fileType?: string | null;
    thread_ts?: string | null;
    senderType: string;
    component?: React.ReactNode;
    ask_action_confirmation?: boolean;
    is_result_interaction?: boolean;
    ask_result_interaction?: boolean;
    source: any;
    graph_data?: any;
    fileData?: any;
    page_numbers?: any;
}

interface RenderMessageProps {
    message: Message;
}

const RenderMessage: React.FC<RenderMessageProps> = ({ message }) => {
    const [hoveredImg, setHoveredImg] = useState(false);
    const [currentIndex, setCurrentIndex] = useState(0);
    const { updateState } = useMyContext();
    const { getAccessTokenSilently } = useAuth0();

    const parseSourceString = (sourceStr: string) => {
        if (!sourceStr) return { title: "", pages: null };
        const match = sourceStr.match(/^(.*?)\s*\(\s*([p\d,\s.-]+)\s*\)$/i);
        if (match) {
            let pagesStr = match[2].replace(/p\.?/gi, '');
            pagesStr = pagesStr.split(',').map(s => s.trim()).filter(Boolean).join(', ');
            return {
                title: match[1].trim(),
                pages: pagesStr ? `Page ${pagesStr}` : null
            };
        }
        return { title: sourceStr, pages: null };
    };

    let sourceObjects: any[] = [];
    let originalSources: string[] = [];
    
    if (Array.isArray(message.source)) {
        sourceObjects = message.source.map((s: any) => ({
            filename: typeof s === 'string' ? s : (s.filename || s.file_name || s.name || ""),
            page_number: typeof s === 'object' && s !== null ? (s.page_number || s.page_numbers || s.pages || null) : null
        })).filter((s: any) => s.filename);
        originalSources = sourceObjects.map(s => s.filename);
    } else if (typeof message.source === 'string' && message.source) {
        let isJsonArray = false;
        try {
            const parsedSource = JSON.parse(message.source);
            if (Array.isArray(parsedSource)) {
                sourceObjects = parsedSource.map((s: any) => ({
                    filename: typeof s === 'string' ? s : (s.filename || s.file_name || s.name || ""),
                    page_number: typeof s === 'object' && s !== null ? (s.page_number || s.page_numbers || s.pages || null) : null
                })).filter((s: any) => s.filename);
                originalSources = sourceObjects.map(s => s.filename);
                isJsonArray = true;
            } else if (parsedSource && typeof parsedSource === 'object') {
                const arr = Array.isArray(parsedSource.source) ? parsedSource.source : [parsedSource];
                sourceObjects = arr.map((s: any) => ({
                    filename: typeof s === 'string' ? s : (s.filename || s.file_name || s.name || ""),
                    page_number: typeof s === 'object' && s !== null ? (s.page_number || s.page_numbers || s.pages || null) : null
                })).filter((s: any) => s.filename);
                originalSources = sourceObjects.map(s => s.filename);
                isJsonArray = true;
            }
        } catch (e) {
            // Not a JSON string, ignore error and fallback to string processing
        }

        if (!isJsonArray) {
            originalSources = message.source.split(/,(?![^()]*\))/g).map(s => s.trim()).filter(Boolean);
            sourceObjects = originalSources.map(s => {
                const parsed = parseSourceString(s);
                return {
                    filename: parsed.title,
                    page_number: parsed.pages
                };
            });
        }
    } else if (typeof message.source === 'object' && message.source !== null) {
        const arr = Array.isArray(message.source.source) ? message.source.source : [message.source];
        sourceObjects = arr.map((s: any) => ({
            filename: typeof s === 'string' ? s : (s.filename || s.file_name || s.name || ""),
            page_number: typeof s === 'object' && s !== null ? (s.page_number || s.page_numbers || s.pages || null) : null
        })).filter((s: any) => s.filename);
        originalSources = sourceObjects.map(s => s.filename);
    }

    const sources = originalSources;

    const findMatchingSourceObject = (docNameStr: string) => {
        if (!docNameStr || !sourceObjects || sourceObjects.length === 0) return null;
        const cleanDoc = docNameStr.toLowerCase().trim();
        const cleanDocWithoutExt = cleanDoc.replace(/\.[^/.]+$/, "").trim();

        // 1. Exact match on filename
        let match = sourceObjects.find((obj: any) => {
            const sName = (obj.filename || "").toLowerCase().trim();
            return sName === cleanDoc;
        });
        if (match) return match;

        // 2. Exact match without extension
        match = sourceObjects.find((obj: any) => {
            const sName = (obj.filename || "").toLowerCase().trim();
            const sWithoutExt = sName.replace(/\.[^/.]+$/, "").trim();
            return Boolean(sWithoutExt && cleanDocWithoutExt && sWithoutExt === cleanDocWithoutExt);
        });
        if (match) return match;

        // 3. Substring match
        match = sourceObjects.find((obj: any) => {
            const sName = (obj.filename || "").toLowerCase().trim();
            const sWithoutExt = sName.replace(/\.[^/.]+$/, "").trim();
            return (
                Boolean(cleanDoc && sName && (cleanDoc.includes(sName) || sName.includes(cleanDoc))) ||
                Boolean(cleanDocWithoutExt && sWithoutExt && (cleanDocWithoutExt.includes(sWithoutExt) || sWithoutExt.includes(cleanDocWithoutExt)))
            );
        });
        return match || null;
    };

    const handleNext = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (currentIndex < sources.length - 1) setCurrentIndex(currentIndex + 1);
    };

    const handlePrev = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (currentIndex > 0) setCurrentIndex(currentIndex - 1);
    };

    const handleDocumentClick = async () => {
        if (sources.length > 0) {
            const fallbackToLocalDocs = () => {
                let fallbackDocs: any[] = [];
                
                // Try to find matching documents in graph_data or fileData
                let availableDocs: any[] = [];
                if (message?.fileData?.documentResult && Array.isArray(message.fileData.documentResult)) {
                    availableDocs = availableDocs.concat(message.fileData.documentResult);
                }
                if (message?.graph_data?.documents && Array.isArray(message.graph_data.documents)) {
                    availableDocs = availableDocs.concat(message.graph_data.documents);
                }
                
                fallbackDocs = sourceObjects.map((obj) => {
                    const sName = obj.filename.toLowerCase().trim();
                    const docObj = availableDocs.find((d: any) => {
                        if (!d) return false;
                        const docName = (d.file_name || d.name || d.title || "").toLowerCase().trim();
                        return docName === sName || docName.includes(sName) || sName.includes(docName);
                    });

                    if (docObj) {
                        return {
                            name: docObj.file_name || docObj.name || docObj.title || obj.filename,
                            url: docObj.downloadable_url || docObj.metadata?.downloadable_url || docObj.url || docObj.file_url || "",
                            updated: docObj.last_modified || docObj.updated || docObj.last_modified_date || docObj.metadata?.last_modified || "2024-11-04",
                            project_id: docObj.metadata?.dossier_id || docObj.dossier_id || docObj.dossier || docObj.project_id || docObj.metadata?.project_id || (docObj.file_id && docObj.file_id.substring(0, 8)) || "N/A",
                            file_type: docObj.file_type || (docObj.file_name || docObj.name || docObj.title || obj.filename).split('.').pop()?.toUpperCase() || "PDF",
                            page_number: obj.page_number || docObj.page_number || docObj.page_numbers || docObj.pages || message.page_numbers || null
                        };
                    } else {
                        return {
                            name: obj.filename,
                            url: "",
                            updated: "2024-11-04",
                            project_id: "N/A",
                            file_type: obj.filename.split('.').pop()?.toUpperCase() || "PDF",
                            page_number: obj.page_number || message.page_numbers || null
                        };
                    }
                });
                
                updateState("kgSidebarData", fallbackDocs);
            };

            try {
                const token = await getAccessTokenSilently();
                const response = await getDocumentsMetadata<any>(sources, token);

                let rawDocs: any[] = [];
                if (response) {
                    if (Array.isArray(response)) {
                        rawDocs = response;
                    } else if (Array.isArray(response.document_result)) {
                        rawDocs = response.document_result;
                    } else if (Array.isArray(response.results)) {
                        rawDocs = response.results;
                    } else if (response.results && typeof response.results === 'object') {
                        const filenames = response.results.top_k_filenames || [];
                        const metadata = response.results.top_k_metadata || response.results.metadata || [];
                        const urls = response.results.downloadable_url || response.results.urls || [];
                        const rootDossierId = response.results.dossier_id || response.results.project_id || response.results.metadata?.dossier_id || "";

                        if (filenames.length > 0 && (metadata.length > 0 || urls.length > 0)) {
                            // Zip filenames with metadata and urls if they are separate
                            rawDocs = filenames.map((name: any, i: number) => {
                                const meta = metadata[i] || {};
                                const url = urls[i] || "";
                                const baseObj = typeof name === 'string' ? { file_name: name } : { ...name };
                                return {
                                    ...baseObj,
                                    ...meta,
                                    downloadable_url: baseObj.downloadable_url || meta.downloadable_url || url || meta.url || "",
                                    dossier_id: baseObj.dossier_id || meta.dossier_id || rootDossierId || ""
                                };
                            });
                        } else {
                            rawDocs = response.results.documents || response.results.top_k_filenames || [];
                        }
                    } else if (Array.isArray(response.data)) {
                        rawDocs = response.data;
                    } else if (response.documents && Array.isArray(response.documents)) {
                        rawDocs = response.documents;
                    }
                }

                if (rawDocs.length > 0) {
                    let mappedDocs = rawDocs.map((doc: any) => {
                        const name = typeof doc === 'string' ? doc : (doc.file_name || doc.name || doc.title || "Unknown File");
                        const matchedSource = findMatchingSourceObject(name);
                        const pageNum = matchedSource?.page_number || (typeof doc === 'object' && (doc.page_number || doc.page_numbers || doc.pages)) || message.page_numbers || null;

                        // Handle if doc is just a string (filename)
                        if (typeof doc === 'string') {
                            return {
                                name: doc,
                                url: "",
                                updated: "2024-11-04",
                                project_id: "N/A",
                                file_type: doc.split('.').pop()?.toUpperCase() || "PDF",
                                page_number: pageNum
                            };
                        }
                        // Handle if doc is an object
                        return {
                            name: doc.file_name || doc.name || doc.title || "Unknown File",
                            url: doc.downloadable_url || doc.metadata?.downloadable_url || doc.url || doc.file_url || "",
                            updated: doc.last_modified || doc.updated || doc.last_modified_date || doc.metadata?.last_modified || "2024-11-04",
                            project_id: doc.metadata?.dossier_id || doc.dossier_id || doc.dossier || doc.project_id || doc.metadata?.project_id || (doc.file_id && doc.file_id.substring(0, 8)) || "N/A",
                            file_type: doc.file_type || (doc.file_name || doc.name || doc.title || "").split('.').pop()?.toUpperCase() || "PDF",
                            page_number: pageNum
                        };
                    });

                    // Strictly filter documents to match the ones shown in the popup
                    if (sourceObjects.length > 0) {
                        mappedDocs = mappedDocs.filter(doc => Boolean(findMatchingSourceObject(doc.name)));

                        // Ensure all sourceObjects exist in mappedDocs so no source files are missed
                        sourceObjects.forEach((obj: any) => {
                            const alreadyExists = mappedDocs.some(doc => {
                                const dName = (doc.name || "").toLowerCase().trim();
                                const sName = (obj.filename || "").toLowerCase().trim();
                                return dName === sName || dName.includes(sName) || sName.includes(dName);
                            });
                            if (!alreadyExists) {
                                mappedDocs.push({
                                    name: obj.filename,
                                    url: "",
                                    updated: "2024-11-04",
                                    project_id: "N/A",
                                    file_type: obj.filename.split('.').pop()?.toUpperCase() || "PDF",
                                    page_number: obj.page_number || null
                                });
                            }
                        });
                    }

                    if (mappedDocs.length > 0) {
                        updateState("kgSidebarData", mappedDocs);
                    } else {
                        fallbackToLocalDocs();
                    }
                } else {
                    fallbackToLocalDocs();
                }
            } catch (error) {
                console.error("Error fetching document metadata:", error);
                fallbackToLocalDocs();
            }
        } else if (message?.graph_data?.documents) {
            const mappedGraphDocs = message.graph_data.documents.map((doc: any) => {
                const name = doc.name || doc.file_name || doc.title || "";
                const matchedSource = findMatchingSourceObject(name);
                return {
                    ...doc,
                    page_number: matchedSource?.page_number || doc.page_number || doc.page_numbers || doc.pages || message.page_numbers || null
                };
            });
            updateState("kgSidebarData", mappedGraphDocs);
        } else {
            updateState("kgSidebarData", []);
        }

        updateState("isKgSidebarOpen", true);
        updateState("isDocLinkSidebarOpen", false);
    };

    const getCurrentPageNumbers = () => {
        if (!message) return null;
        
        const currentObj = sourceObjects[currentIndex];
        if (currentObj && currentObj.page_number) {
            let pages = currentObj.page_number;
            if (Array.isArray(pages)) {
                return `Page ${pages.join(', ')}`;
            }
            if (typeof pages === 'string') {
                const lower = pages.toLowerCase();
                if (lower.startsWith('page')) return pages;
                return `Page ${pages}`;
            }
            return `Page ${pages}`;
        }

        const currentSource = sources[currentIndex];
        if (!currentSource) return null;

        const parsed = parseSourceString(currentSource);
        if (parsed.pages) {
            return parsed.pages;
        }

        const currentSourceLower = parsed.title.toLowerCase().trim();
        
        let docs: any[] = [];
        if (message.fileData?.documentResult && Array.isArray(message.fileData.documentResult)) {
            docs = docs.concat(message.fileData.documentResult);
        }
        if (message.graph_data?.documents && Array.isArray(message.graph_data.documents)) {
            docs = docs.concat(message.graph_data.documents);
        }

        const doc = docs.find((d: any) => {
            if (!d) return false;
            const docName = (d.file_name || d.name || d.title || "").toLowerCase().trim();
            return docName === currentSourceLower || docName.includes(currentSourceLower) || currentSourceLower.includes(docName);
        });

        let pages: any = null;
        if (doc) {
            pages = doc.page_numbers || doc.pages || doc.page || null;
        }
        
        if (!pages && message.page_numbers) {
            pages = message.page_numbers;
        }

        if (!pages) return null;

        if (Array.isArray(pages)) {
            return `Page ${pages.join(', ')}`;
        }
        if (typeof pages === 'string') {
            const lower = pages.toLowerCase();
            if (lower.startsWith('page')) return pages;
            return `Page ${pages}`;
        }
        return `Page ${pages}`;
    };

    const data = (message: Message) => {
        if (message.senderType === "admin" && message.text) {
            return (
                <>
                    {message.source ? (
                        <div className="source-img-content-section"
                            onMouseEnter={() => setHoveredImg(true)}
                            onMouseLeave={() => setHoveredImg(false)}
                        >
                            <img
                                className="source-image"
                                width={"13"}
                                height={"13"}
                                alt="avatar"
                                src={
                                    hoveredImg
                                        ? "svg/hover/source-hover.jpg"
                                        : "svg/source.png"
                                }
                                style={{ cursor: 'pointer' }}
                                onClick={handleDocumentClick}
                            />
                            {hoveredImg && sources.length > 0 ? (
                                <div className="professional-citation-card">
                                    <div className="card-header">
                                        <span className="file-count">
                                            {sources.length > 1 ? (
                                                <>
                                                    <span onClick={handlePrev} style={{ cursor: currentIndex > 0 ? 'pointer' : 'default', opacity: currentIndex > 0 ? 1 : 0.5, color: currentIndex > 0 ? 'black' : 'inherit' }}>&lt;</span>
                                                    {' '}{currentIndex + 1} / {sources.length} files{' '}
                                                    <span onClick={handleNext} style={{ cursor: currentIndex < sources.length - 1 ? 'pointer' : 'default', opacity: currentIndex < sources.length - 1 ? 1 : 0.5, color: currentIndex < sources.length - 1 ? 'black' : 'inherit' }}>&gt;</span>
                                                </>
                                            ) : (
                                                `1 file`
                                            )}
                                        </span>
                                        <span className="file-badge-mini">PDF</span>
                                    </div>
                                    <div
                                        className="card-title"
                                        onClick={handleDocumentClick}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        {sourceObjects[currentIndex]?.filename}
                                    </div>
                                    {getCurrentPageNumbers() && (
                                        <div className="card-pages" style={{ fontSize: '13px', color: '#333', marginTop: '4px' }}>
                                            {getCurrentPageNumbers()}
                                        </div>
                                    )}
                                </div>
                            ) : null}
                        </div>
                    ) : null}
                    {message?.file ? <Pdf message={message} /> : null}
                    <div className="message-text" dangerouslySetInnerHTML={{ __html: formatMessageText(message.text) }} />
                </>
            );
        }
        if (
            message.fileType === "application/pdf" ||
            message.fileType === "text/plain" ||
            message.fileType === "application/msword" ||
            message.fileType === "text/csv" ||
            message.fileType === "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
            message.fileType === "image/jpeg" ||
            message.fileType === "image/png" ||
            message.fileType === "application/vnd.ms-excel" ||
            message.fileType === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        ) {
            return <Pdf message={message} />;
        }

        return <p>{message.text}</p>;
    };

    return <>{data(message)}</>;
};

export default RenderMessage;
